// Batch Lifecycle Demo v2.1 — event detector. Pure: no DOM, no timers, no Date/Math.random.
// Spec: xl-specs/specs/2026-10-14-batch-demo-v2-1-guide-dock-design.md §5b
//
// The detector sees only what a real edge system would: signal snapshots (fed with feed(t, snapshot), sampled
// internally at 1 Hz) and the plant's exported events (onPlantEvent). It never reads model state. Everything it
// says comes from its own history: close-time baselines, the idle shock baseline, the tank's level slope over one
// 2 min between transfers. Its output is a list of DetectedEvents, each with an interpretation, the evidence it used, and
// cause/effect links built from time order plus a plant graph:
//   line → tank → transfer → stations;  dribble valve → dose;  mixer → shock.

import { CONSTANTS as C, PHASES } from "./sim.js"

/** What the detector reads: the signal suffixes per station / tank and the exported plant event types. Nothing else. */
export const DETECTOR_READS = Object.freeze({
  unitSignals: ["rpm", "shock_pk", "plc_rms", "phase", "hold", "xv02_tclose"],
  tankSignals: ["level_pct", "permissive", "interlock", "line_run"],
  events: ["unit.phase", "dose.verify", "preact.trim", "batch.start", "batch.complete", "tank.interlock", "wear.rebuilt"],
})

/** Equipment limits, from the plant's published constants (what an edge system is configured with). */
export const LIMITS = Object.freeze({
  shockG: C.SHOCK_LIMIT_G,                                       // 3.2 g
  valveLifeS: C.VALVE_T_CLOSE * (1 + C.WEAR_FACTOR_AT_LIFE),     // 4.75 s: dribble close time at end of life
  dueBatches: C.WEAR_DUE_BATCHES,                                // flag a rebuild projected within 40 batches
  permissivePct: C.TANK_PERMISSIVE_PCT,                          // 72 %
})

/** Rule parameters (measured values that justify them are in the Task 2 report). */
export const RULES = Object.freeze({
  sampleS: 1,                 // internal sampling period
  windowS: 600,               // cause → effect window (10 min)
  cap: 2000,                  // events kept
  baselineN: 50,              // close-time baseline: median of the last 50 strokes
  baselineMinN: 3,            // ...once there are at least 3
  slowCloseRatio: 1.5,        // valve.slow_close: stroke > 1.5 × baseline
  outlierRatio: 2,            // a stroke > 2 × the median is a fault, not wear: kept out of the history
  wearFitN: 60,               // wear.projection: least squares over the last 60 strokes
  wearMinN: 10,
  shockSustainS: 2,           // shock.cavitation: above the limit ≥ 2 s while mixing...
  rpmSagFrac: 0.9,            // ...with the hold speed sagging below 90 % of the mix plateau
  shockEndSamples: 2,         // an episode ends after 2 samples below the limit
  idleWindowS: 600,           // shock baseline: median of non-mixing (rpm == 0) samples, last 10 min
  tankWindowS: 120,           // tank.rate_change: mean level slope between transfers over the last 2 min...
  tankMinCleanS: 30,          // ...from at least 30 transfer-free seconds (nominal: never fewer than 47)
  tankPlateau: -0.2,          // not falling (>= -0.2 %/min) while the line is expected to draw (nominal: -6.25 always)
  tankResume: -1.0,           // falling again (< -1 %/min) after such a plateau
  mergeS: 60,                 // a recurrence within 60 s of the previous same class + unit event extends it
})

/**
 * Classes never merged: each batch and each dose verify is its own record (a dose recovery chain points at them), and a
 * state edge (line, operator, high-high) is one edge with its own duration. Recurrences merge for holds, shock, tank rate.
 */
const NO_MERGE = new Set(["batch.complete", "dose.overshoot", "dose.undershoot",
  "line.stopped", "line.restarted", "operator.hold", "operator.rebuild", "tank.hihi"])

export const LANE_BY_CLASS = Object.freeze({
  "dose.overshoot": "wt", "dose.undershoot": "wt",
  "shock.peak": "shock", "shock.cavitation": "shock",
  "valve.slow_close": "flow", "wear.projection": "wt",        // the slow stroke shows on the feed lane
  "tank.rate_change": "tank", "transfer.held": "tank", "tank.hihi": "tank",
  "line.stopped": "tank", "line.restarted": "tank",
  "operator.hold": "wt", "operator.rebuild": "wt",
  "batch.complete": null,
})

const SEVERITY = {
  "dose.overshoot": "warning", "dose.undershoot": "warning", "shock.peak": "warning", "shock.cavitation": "alarm",
  "valve.slow_close": "warning", "wear.projection": "warning", "tank.rate_change": "info", "transfer.held": "warning",
  "tank.hihi": "alarm", "line.stopped": "warning", "line.restarted": "info", "operator.hold": "info",
  "operator.rebuild": "info", "batch.complete": "info",
}

// ---- formatting ----
const kg = (x) => `${x >= 0 ? "+" : "-"}${Math.abs(x).toFixed(2)} kg`
const sec = (s) => `${s < 2 ? s.toFixed(2) : s.toFixed(1)} s`
const pct = (p) => `${p.toFixed(1)} %`
const plural = (n, w, ws = w + "s") => `${n} ${n === 1 ? w : ws}`
const WORDS = ["zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten", "eleven", "twelve"]
export function clock(t) {
  const a = Math.round(Math.abs(t)), h = Math.floor(a / 3600), m = Math.floor((a % 3600) / 60), s = a % 60
  const ss = String(s).padStart(2, "0")
  return `T${t < 0 ? "-" : "+"}${h ? `${h}:${String(m).padStart(2, "0")}:${ss}` : `${m}:${ss}`}`
}
function dur(s) {
  const a = Math.round(s)
  if (a < 60) return `${a} s`
  if (a < 3600) return `${Math.floor(a / 60)} min ${a % 60} s`
  return `${Math.floor(a / 3600)} h ${Math.floor((a % 3600) / 60)} min`
}

/** Interpretation templates, one per class: plain language, numbers from the evidence, no vendor names. */
const TEMPLATES = {
  "dose.overshoot": doseText,
  "dose.undershoot": doseText,
  "valve.slow_close": (d) => `${d.valve} closed in ${sec(d.stroke)}, ${d.ratio.toFixed(1)}× its baseline.`,
  "shock.cavitation": (d) =>
    `${d.unit} shock ${d.peak.toFixed(1)} g for ${dur(d.dur)}${spells(d)} while mixing, rpm sagged ${d.plateau}→${d.minRpm}. ` +
    `Consistent with cavitation. The controller's scan read ${d.scan.toFixed(1)} g.`,
  "shock.peak": (d) =>
    `${d.unit} shock ${d.peak.toFixed(1)} g for ${dur(d.dur)}${spells(d)}${d.mixing ? " while mixing" : ""}, above the ${d.limit} g limit` +
    `${Number.isFinite(d.idle) ? ` (idle baseline ${d.idle.toFixed(1)} g)` : ""}. The controller's scan read ${d.scan.toFixed(1)} g.`,
  "wear.projection": (d) =>
    (d.now >= d.life
      ? `${d.valve} rebuild overdue: closing in ${d.now.toFixed(2)} s, past its ${d.life.toFixed(2)} s life limit.`
      : `${d.valve} rebuild due in ${plural(d.due, "batch", "batches")}, projected from the close-time trend ` +
        `(${d.now.toFixed(2)} s now, life limit ${d.life.toFixed(2)} s).`) +
    `${d.clearedAt != null ? ` Cleared by the rebuild at ${clock(d.clearedAt)}.` : ""}`,
  "tank.rate_change": (d) => {
    const since = d.since ? ` since the filling line ${d.since}` : ""
    if (d.kind === "fall") return `TK-200 falling again at ${d.rate.toFixed(1)} %/min between transfers${since}.`
    return d.net > 0.2
      ? `TK-200 rising at +${d.net.toFixed(1)} %/min${since}; between transfers it no longer falls${was(d)}.`
      : `TK-200 holding at ${pct(d.levelT0)}${since}; between transfers it no longer falls${was(d)}.`
  },
  "transfer.held": (d) => d.t1 == null
    ? `Transfers held${d.episodes > 1 ? " again" : ""}: TK-200 at ${pct(d.levelNow)}, ${plural(d.n, "station")} waiting. ${d.clears}`
    : `Transfers held for ${dur(heldDur(d))}${spells(d, "spells")}: TK-200 ${d.episodes > 1 ? "up to" : "at"} ${pct(d.level)}, ` +
      `up to ${plural(d.maxN, "station")} waiting. Cleared at ${clock(d.t1)}.`,
  "tank.hihi": (d) => `High-high trip at ${pct(d.level)}: outlets closed.${d.episodes > 1 ? ` Tripped ${d.episodes} times.` : ""}`,
  "line.stopped": (d) =>
    `Filling line stopped at ${clock(d.t0)}, TK-200 at ${pct(d.level)}.${d.t1 != null ? ` Restarted after ${dur(d.t1 - d.t0)}.` : ""}`,
  "line.restarted": (d) =>
    `Filling line restarted at ${clock(d.t0)}${d.after != null ? ` after ${dur(d.after)} stopped` : ""}, TK-200 at ${pct(d.level)}.`,
  "operator.hold": (d) => `${d.unit} held by operator at ${clock(d.t0)}${d.t1 != null ? `, resumed after ${dur(d.t1 - d.t0)}` : ""}.`,
  "operator.rebuild": (d) =>
    `${d.unit} dribble valve rebuilt.${Number.isFinite(d.close) ? ` Close time ${sec(d.close)}.` : ""}${d.cleared ? " Projection cleared." : ""}`,
  "batch.complete": (d) => `Batch ${d.batchId}: ${d.kg.toFixed(1)} kg, ${d.doses}${d.dur != null ? `, ${dur(d.dur)}` : ""}.`,
}

/** Merged recurrences say how many spells they cover. */
/** A merged hold counts only its spells, like a merged shock: the gaps between them were not held. */
const heldDur = (d) => (d.prevDur || 0) + ((d.t1 ?? d.spellT0) - d.spellT0)
const was = (d) => (Number.isFinite(d.was) ? ` (it was falling ${Math.abs(d.was).toFixed(1)} %/min)` : "")
const spells = (d, word = "spells") => (d.episodes > 1 ? ` in ${d.episodes} ${word}` : "")

function doseText(d) {
  let s = `${d.unit} ${kg(d.err)}.`
  if (Number.isFinite(d.stroke)) {
    s += Number.isFinite(d.base)
      ? ` Dribble valve closed in ${sec(d.stroke)} against a ${sec(d.base)} baseline.`
      : ` Dribble valve closed in ${sec(d.stroke)}; no close-time baseline yet.`
  }
  if (d.follows) s += ` Follows the ${kg(d.follows.err)} ${d.follows.word}'s preact trim.`
  if (d.trim != null) s += Math.abs(d.trim) < 0.005 ? ` Preact already at its ${d.trimTo.toFixed(2)} kg limit: no trim left.` : ` Preact trimmed ${kg(d.trim)}.`
  if (d.backIn === 1) s += " Next dose in tolerance."
  else if (d.backIn > 1) s += ` Back in tolerance ${d.backIn} doses later.`
  return s
}

function median(xs) {
  const s = xs.slice().sort((a, b) => a - b), m = s.length >> 1
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2
}

const MIX = new Set(["MIX_RAMP", "MIX_HOLD", "MIX_COAST"])
const IDLE_N = 600

export function createDetector({ signals, signalIndex, limits = LIMITS, rules = RULES } = {}) {
  const index = signalIndex || Object.fromEntries(signals.map((s, i) => [s.key, i]))
  const col = (k) => (index[k] == null ? -1 : index[k])
  const unitIds = signals.map((s) => s.key).filter((k) => /^ST-\d+\.wt$/.test(k)).map((k) => k.slice(0, -3))
  const listeners = new Set()
  const events = []
  const byId = new Map()
  const data = new Map()          // id → template data (the interpretation is re-rendered from it on update)
  const recent = new Map()        // class → last 20 events of that class, for cause search
  let seq = 0, lastT = null

  const units = new Map()
  for (const id of unitIds) {
    units.set(id, {
      id, valve: `XV-${id.split("-")[1]}-2`,
      ix: Object.fromEntries(DETECTOR_READS.unitSignals.map((s) => [s, col(`${id}.${s}`)])),
      rpm: NaN, shock: NaN, plc: NaN, phase: null, hold: NaN, tclose: NaN, lastHold: NaN,
      settleT: null, latchPending: false, stroke: NaN, strokes: [], recovery: [], pendingDose: null,
      ep: null, plateau: 0, idleT: new Float64Array(IDLE_N), idleV: new Float64Array(IDLE_N), idleI: 0, idleN: 0,
      holdEv: null, wearEv: null, rebuildAt: null,
    })
  }
  const tk = {
    ix: Object.fromEntries(DETECTOR_READS.tankSignals.map((s) => [s, col(`TK-200.${s}`)])),
    level: NaN, permissive: NaN, interlock: NaN, lastLine: NaN, lastInterlock: NaN,
    // tank.rate_change: level deltas over transfer-free seconds (no station in DRAIN/EMPTY), and the raw level, last 2 min
    clean: [], cleanSum: 0, levels: [], prevL: NaN, prevT: null, flow: "falling", falls: [], resumeSum: 0, resumeN: 0,
    lastFallT: null, firstFallT: null, lastDrainT: -Infinity, lineKnown: false, lastLineTrueT: -Infinity, rateEv: null,
    lineEv: null, restartEv: null, heldEv: null, heldPendT: null, hihiEv: null, hihiPendT: null, hihiClearT: null,
  }
  const waiting = new Set()                    // stations in WAIT_TRANSFER, from unit.phase events
  const draining = new Set()                   // stations in DRAIN/EMPTY (outlet flowing), from unit.phase events
  let phasesSeen = false
  const batchStart = new Map(), batchDoses = new Map()

  // ---- event plumbing ----
  function make(cls, unit, t0, t1, d, sigs, evidence) {
    const ev = {
      id: `d${++seq}`, class: cls, severity: SEVERITY[cls], unit, t0, lane: LANE_BY_CLASS[cls],
      signals: sigs.filter((k) => index[k] != null), interpretation: "",
      evidence: evidence.filter((r) => r && index[r.signal] != null && Number.isFinite(r.value)),
      causes: [], effects: [],
    }
    // Open-event contract: while the episode runs `open` is true and `t1` is null; the end time is set when it closes.
    // The running end is kept privately (`_t1`) because merging and cause search compare against it.
    Object.defineProperty(ev, "_t1", { value: t1, writable: true, enumerable: false })
    Object.defineProperty(ev, "t1", { enumerable: true, get() { return dataOf(this.id).open ? null : this._t1 }, set(v) { this._t1 = v } })
    Object.defineProperty(ev, "open", { enumerable: true, get() { return !!dataOf(this.id).open } })
    data.set(ev.id, d)
    return ev
  }
  function render(ev) { ev.interpretation = TEMPLATES[ev.class](data.get(ev.id)) }
  const dataOf = (id) => data.get(id) || {}      // an event evicted by the cap may still be held by rule state
  function emit(ev) {
    const prev = mergeTarget(ev)
    if (prev) return merge(prev, ev)
    render(ev)
    events.push(ev); byId.set(ev.id, ev)
    while (events.length > rules.cap) { const old = events.shift(); byId.delete(old.id); data.delete(old.id) }
    let r = recent.get(ev.class); if (!r) recent.set(ev.class, (r = []))
    r.push(ev); if (r.length > 20) r.shift()
    for (const fn of listeners) fn(ev)
    return ev
  }
  function update(ev) {
    if (!byId.has(ev.id)) return
    render(ev)
    for (const fn of listeners) fn(ev, { updated: true })
  }
  function link(cause, effect) {
    if (!cause || !effect || cause === effect || cause.effects.includes(effect.id) || effect.effects.includes(cause.id)) return
    cause.effects.push(effect.id); effect.causes.push(cause.id)
  }
  // ---- recurrence merging: the same class + unit again within mergeS of the last one's end extends that one ----
  const mergeKey = (ev) => (ev.class === "tank.rate_change" ? dataOf(ev.id).kind : "")
  function mergeTarget(ev) {
    if (NO_MERGE.has(ev.class)) return null
    const r = recent.get(ev.class) || []
    for (let i = r.length - 1; i >= 0; i--) {
      const p = r[i]
      if (!byId.has(p.id) || p.unit !== ev.unit) continue
      return mergeKey(p) === mergeKey(ev) && ev.t0 >= p.t0 && ev.t0 - p._t1 <= rules.mergeS ? p : null
    }
    return null
  }
  const MERGERS = {
    "transfer.held": (pd, nd) => ({ maxN: Math.max(pd.maxN, nd.maxN), level: Math.max(pd.level, nd.level), levelStart: pd.levelStart, prevDur: heldDur(pd) }),
    "tank.hihi": (pd, nd) => ({ level: Math.max(pd.level, nd.level) }),
    "shock.cavitation": (pd) => ({ base: shockBase(pd) }),
    "shock.peak": (pd) => ({ base: shockBase(pd) }),
  }
  function merge(prev, ev) {
    const pd = dataOf(prev.id), nd = dataOf(ev.id)
    const md = { ...nd, t0: pd.t0 ?? nd.t0, episodes: (pd.episodes || 1) + 1, ...(MERGERS[ev.class] ? MERGERS[ev.class](pd, nd) : {}) }
    data.set(prev.id, md); data.delete(ev.id)
    if (md.base) shockDerive(md)
    prev.t1 = Math.max(prev._t1, ev._t1)
    prev.evidence = ev.evidence.map((r) => ({ ...r, window: [Math.min(r.window[0], prev.t0), r.window[1]] }))
    for (const s of ev.signals) if (!prev.signals.includes(s)) prev.signals.push(s)
    if (prev.class === "transfer.held") heldRows(prev)
    // Links made for the new detection move to the surviving event.
    for (const id of ev.causes) { const c = byId.get(id); if (c) { c.effects = c.effects.filter((x) => x !== ev.id); link(c, prev) } }
    for (const id of ev.effects) { const f = byId.get(id); if (f) { f.causes = f.causes.filter((x) => x !== ev.id); link(prev, f) } }
    update(prev)
    return prev
  }
  /** Newest event of a class that precedes t and is open or ended within the window. */
  function causeOf(cls, t, pred = () => true) {
    const r = recent.get(cls) || []
    for (let i = r.length - 1; i >= 0; i--) {
      const e = r[i]
      if (!byId.has(e.id) || e.t0 > t + 1e-6 || !pred(e)) continue      // tolerance: live model time drifts by ~1e-13
      const d = dataOf(e.id)
      if (d.open || t - e._t1 <= rules.windowS) return e
    }
    return null
  }

  // ---- 1 Hz sampling ----
  function feed(t, snap) {
    if (lastT !== null && t - lastT < rules.sampleS - 1e-3) return
    lastT = t
    for (const u of units.values()) if (u.pendingDose) { emit(u.pendingDose); u.pendingDose = null }
    for (const u of units.values()) sampleUnit(u, t, snap)
    sampleTank(t, snap)
  }
  const rd = (snap, i) => (i < 0 ? NaN : snap[i])

  function sampleUnit(u, t, snap) {
    const ix = u.ix
    u.rpm = rd(snap, ix.rpm); u.shock = rd(snap, ix.shock_pk); u.plc = rd(snap, ix.plc_rms)
    u.hold = rd(snap, ix.hold); u.tclose = rd(snap, ix.xv02_tclose)
    // The stroke this dose got: the close-time reading at the first sample after the close command. Read later (at the
    // verify) it could already be the next stroke's value, e.g. a fault injected while the dose settles.
    if (u.latchPending && Number.isFinite(u.tclose)) { u.stroke = u.tclose; u.latchPending = false }
    const p = rd(snap, ix.phase)
    const phase = Number.isFinite(p) ? PHASES[p] : null

    if (u.rebuildAt != null) rebuildDone(u)
    if (Number.isFinite(u.hold)) {
      if (u.hold === 1 && u.lastHold === 0) holdStart(u, t)
      else if (u.hold === 0 && u.lastHold === 1 && u.holdEv) holdEnd(u, t)
      u.lastHold = u.hold
    }
    if (Number.isFinite(u.shock) && Number.isFinite(u.rpm)) shockStep(u, t, phase)
  }

  // ---- shock: shock.peak / shock.cavitation ----
  function shockStep(u, t, phase) {
    const mixing = phase ? MIX.has(phase) : u.rpm > 0
    if (phase === "MIX_RAMP" || phase === "MIX_HOLD") u.plateau = Math.max(u.plateau, u.rpm)
    else if (!mixing) u.plateau = 0
    if (u.rpm === 0) { u.idleT[u.idleI] = t; u.idleV[u.idleI] = u.shock; u.idleI = (u.idleI + 1) % IDLE_N; if (u.idleN < IDLE_N) u.idleN++ }
    if (u.shock > limits.shockG) {
      let ep = u.ep
      if (!ep) ep = u.ep = { t0: t, tLast: t, peak: -1, peakT: t, scan: NaN, mixing: false, plateau: 0, minHold: Infinity, below: 0, ev: null }
      ep.tLast = t; ep.below = 0
      if (u.shock > ep.peak) { ep.peak = u.shock; ep.peakT = t; ep.scan = u.plc }
      if (mixing) ep.mixing = true
      if (u.plateau > ep.plateau) ep.plateau = u.plateau
      if (phase === "MIX_HOLD") ep.minHold = Math.min(ep.minHold, u.rpm)
      const d = ep.tLast - ep.t0 + rules.sampleS
      if (!ep.ev && ep.mixing && d >= rules.shockSustainS && ep.minHold < rules.rpmSagFrac * ep.plateau) {
        ep.ev = make("shock.cavitation", u.id, ep.t0, t, {}, shockSignals(u), [])
        dataOf(ep.ev.id).open = true
        fillShock(u, ep, t)
        ep.ev = emit(ep.ev)
      } else if (ep.ev) ep.ev.t1 = t
    } else if (u.ep && ++u.ep.below >= rules.shockEndSamples) {
      const ep = u.ep; u.ep = null
      if (ep.ev) { ep.ev.t1 = ep.tLast; fillShock(u, ep, ep.tLast); dataOf(ep.ev.id).open = false; update(ep.ev) }
      else { const ev = make("shock.peak", u.id, ep.t0, ep.tLast, {}, shockSignals(u), []); fillShock(u, ep, ep.tLast, ev); emit(ev) }
    }
  }
  const shockSignals = (u) => [`${u.id}.shock_pk`, `${u.id}.plc_rms`, `${u.id}.rpm`]
  function idleBaseline(u, t) {
    const xs = []
    for (let k = 0; k < u.idleN; k++) if (u.idleT[k] >= t - rules.idleWindowS) xs.push(u.idleV[k])
    return xs.length ? median(xs) : NaN
  }
  /** This spell's figures into the event's data, combined with earlier merged spells (d.base). */
  function fillShock(u, ep, t1, ev = ep.ev) {
    const d = dataOf(ev.id)
    Object.assign(d, {
      unit: u.id, limit: limits.shockG, idle: idleBaseline(u, ep.t0),
      spell: { dur: t1 - ep.t0 + rules.sampleS, peak: ep.peak, scan: ep.scan, peakT: ep.peakT, mixing: ep.mixing, plateau: ep.plateau, minHold: ep.minHold },
    })
    shockDerive(d)
    ev.evidence = [
      { signal: `${u.id}.shock_pk`, window: [ev.t0, t1], value: d.peak, unit: "g", label: "Edge peak" },
      { signal: `${u.id}.plc_rms`, window: [d.peakT, d.peakT], value: d.scan, unit: "g", label: "Controller scan at the peak" },
      Number.isFinite(d.minRpm) ? { signal: `${u.id}.rpm`, window: [ev.t0, t1], value: d.minRpm, unit: "rpm", label: `Hold speed (mix plateau ${d.plateau} rpm)` } : null,
      { signal: `${u.id}.shock_pk`, window: [ep.t0 - rules.idleWindowS, ep.t0], value: d.idle, unit: "g", label: "Idle baseline (median, last 10 min, agitator stopped)" },
    ].filter((r) => r && Number.isFinite(r.value))
  }
  const shockBase = (pd) => ({ episodes: pd.episodes || 1, dur: pd.dur, peak: pd.peak, scan: pd.scan, peakT: pd.peakT, mixing: pd.mixing, plateau: pd.plateau, minRpm: pd.minRpm })
  function shockDerive(d) {
    const b = d.base, e = d.spell
    if (!e) return
    const fromBase = !!b && b.peak >= e.peak
    d.episodes = (b ? b.episodes : 0) + 1
    d.dur = (b ? b.dur : 0) + e.dur
    d.peak = fromBase ? b.peak : e.peak; d.scan = fromBase ? b.scan : e.scan; d.peakT = fromBase ? b.peakT : e.peakT
    d.mixing = (!!b && b.mixing) || e.mixing
    d.plateau = Math.round(Math.max(b ? b.plateau : 0, e.plateau))
    d.minRpm = Math.round(Math.min(b && Number.isFinite(b.minRpm) ? b.minRpm : Infinity, e.minHold))
  }

  // ---- operator actions ----
  function holdStart(u, t) {
    const ev = make("operator.hold", u.id, t, t, { unit: u.id, t0: t, t1: null, open: true }, [`${u.id}.hold`],
      [{ signal: `${u.id}.hold`, window: [t, t], value: 1, unit: "", label: "Hold" }])
    if (waiting.has(u.id)) link(causeOf("transfer.held", t), ev)     // only a station the hold kept waiting
    u.holdEv = emit(ev)
  }
  function holdEnd(u, t) {
    const ev = u.holdEv; u.holdEv = null
    const d = dataOf(ev.id); d.t1 = t; d.open = false; ev.t1 = t
    ev.evidence[0].window = [ev.t0, t]
    update(ev)
  }
  function rebuildDone(u) {
    const t = u.rebuildAt; u.rebuildAt = null
    const proj = u.wearEv; u.wearEv = null
    const ev = make("operator.rebuild", u.id, t, t, { unit: u.id, close: u.tclose, cleared: !!proj }, [`${u.id}.xv02_tclose`],
      [{ signal: `${u.id}.xv02_tclose`, window: [t, t], value: u.tclose, unit: "s", label: "Close time after the rebuild" }])
    link(ev, proj)     // the rebuild clears the projection
    emit(ev)
    if (proj) { const d = dataOf(proj.id); d.clearedAt = t; d.open = false; proj.t1 = t; update(proj) }
  }

  // ---- tank, line, transfers ----
  function sampleTank(t, snap) {
    const ix = tk.ix
    tk.level = rd(snap, ix.level_pct); tk.permissive = rd(snap, ix.permissive)
    const interlock = rd(snap, ix.interlock), line = rd(snap, ix.line_run)
    tk.interlock = interlock
    if (Number.isFinite(line)) {
      tk.lineKnown = true
      if (line === 1) tk.lastLineTrueT = t
      if (tk.lastLine === 1 && line === 0) lineStopped(t)
      else if (tk.lastLine === 0 && line === 1) lineRestarted(t)
      tk.lastLine = line
    }
    // High-high: the plant's interlock event gives the exact time; the level is read from this sample.
    if (Number.isFinite(interlock)) {
      if (interlock === 1 && tk.lastInterlock === 0 && !tk.hihiEv && tk.hihiPendT == null) tk.hihiPendT = t
      if (tk.hihiPendT != null && Number.isFinite(tk.level)) hihiOpen(tk.hihiPendT)
      if (interlock === 0 && tk.hihiEv) hihiClose(t)
      tk.lastInterlock = interlock
    }
    if (Number.isFinite(tk.level)) rateStep(t)
    heldSample(t)
  }

  function lineStopped(t) {
    const ev = make("line.stopped", "TK-200", t, t, { t0: t, t1: null, level: tk.level, open: true },
      ["TK-200.line_run", "TK-200.level_pct"],
      [{ signal: "TK-200.line_run", window: [t, t], value: 0, unit: "", label: "Line running" },
        { signal: "TK-200.level_pct", window: [t, t], value: tk.level, unit: "%", label: "Tank level" }])
    tk.lineEv = emit(ev)
  }
  function lineRestarted(t) {
    const stopped = tk.lineEv; tk.lineEv = null
    if (stopped) {
      const d = dataOf(stopped.id); d.t1 = t; d.open = false; stopped.t1 = t
      stopped.evidence[0].window = [stopped.t0, t]
      update(stopped)
    }
    const ev = make("line.restarted", "TK-200", t, t, { t0: t, after: stopped ? t - stopped.t0 : null, level: tk.level },
      ["TK-200.line_run", "TK-200.level_pct"],
      [{ signal: "TK-200.line_run", window: [t, t], value: 1, unit: "", label: "Line running" },
        { signal: "TK-200.level_pct", window: [t, t], value: tk.level, unit: "%", label: "Tank level" }])
    tk.restartEv = emit(ev)
  }

  function hihiOpen(t) {
    tk.hihiPendT = null
    const ev = make("tank.hihi", "TK-200", t, t, { level: tk.level, open: true }, ["TK-200.interlock", "TK-200.level_pct"],
      [{ signal: "TK-200.interlock", window: [t, t], value: 1, unit: "", label: "High-high interlock" },
        { signal: "TK-200.level_pct", window: [t, t], value: tk.level, unit: "%", label: "Tank level at the trip" }])
    // Held transfers released together, or a stopped line, fill the tank past high-high.
    link(causeOf("transfer.held", t, (e) => e.t0 < t) || causeOf("line.stopped", t), ev)
    tk.hihiEv = emit(ev)
    if (tk.hihiClearT != null) { const c = tk.hihiClearT; tk.hihiClearT = null; hihiClose(c) }
  }
  function hihiClose(t) {
    const ev = tk.hihiEv; tk.hihiEv = null
    dataOf(ev.id).open = false; ev.t1 = t; ev.evidence[0].window = [ev.t0, t]
    update(ev)
  }

  /**
   * The level between transfers falls at the line's draw (-6.25 %/min nominal, every window). A 2 min mean slope over
   * transfer-free seconds that stops falling while the line was drawing is a plateau; a fall that resumes after it, a
   * restart. Transfer seconds (a station in DRAIN/EMPTY, from the plant's phases) are left out: the drain sawtooth is
   * not a rate change.
   */
  function rateStep(t) {
    const W = rules.tankWindowS, L = tk.level
    if (draining.size) tk.lastDrainT = t
    tk.levels.push([t, L]); while (tk.levels[0][0] < t - rules.windowS) tk.levels.shift()
    if (phasesSeen && tk.prevT != null && t - tk.prevT <= 1.5 * rules.sampleS && tk.lastDrainT <= tk.prevT + 1e-6) {
      const dl = L - tk.prevL
      tk.clean.push([t, dl]); tk.cleanSum += dl
      if (dl < -0.01) {
        tk.lastFallT = t
        tk.falls.push(dl); if (tk.falls.length > 30) tk.falls.shift()          // the draw rate: recent falling seconds
        if (tk.flow === "plateau" && tk.firstFallT == null) { tk.firstFallT = t; tk.resumeSum = 0; tk.resumeN = 0 }
      }
      if (tk.flow === "plateau" && tk.firstFallT != null) { tk.resumeSum += dl; tk.resumeN++ }
    }
    tk.prevL = L; tk.prevT = t
    while (tk.clean.length && tk.clean[0][0] <= t - W) tk.cleanSum -= tk.clean.shift()[1]
    if (tk.rateEv) tk.rateEv.t1 = t
    if (tk.clean.length < rules.tankMinCleanS) return
    const rate = (tk.cleanSum / tk.clean.length) * 60
    const first = levelAt(t - W) || tk.levels[0]
    const net = t - first[0] >= W / 2 ? ((L - first[1]) / (t - first[0])) * 60 : 0
    const rows = (t0) => [
      { signal: "TK-200.level_pct", window: [t - W, t], value: rate, unit: "%/min", label: `Level slope between transfers (last ${W / 60} min, ${tk.clean.length} s)` },
      { signal: "TK-200.level_pct", window: [first[0], t], value: net, unit: "%/min", label: "Net level slope, transfers included" },
      { signal: "TK-200.level_pct", window: [t0, t0], value: (levelAt(t0) || [t0, L])[1], unit: "%", label: "Tank level at the change" },
    ]
    const drawRate = () => (tk.falls.length ? (tk.falls.reduce((a, x) => a + x, 0) / tk.falls.length) * 60 : NaN)
    if (tk.flow === "falling") {
      if (rate < rules.tankPlateau) return
      tk.flow = "plateau"; tk.firstFallT = null
      // The first second not falling. If the line is seen stopped now, not before the first second after it was last
      // seen drawing (while it drew, the level was falling); never after this sample.
      const notFalling = tk.lastFallT != null ? tk.lastFallT + rules.sampleS : tk.clean[0][0]
      const t0 = Math.min(t, tk.lastLine === 0 ? Math.max(notFalling, tk.lastLineTrueT + rules.sampleS) : notFalling)
      const expected = !tk.lineKnown || tk.lastLineTrueT >= t0 - W      // the line was drawing (or we cannot tell)
      if (!expected) return
      const cause = causeOf("line.stopped", t0)
      const was = drawRate(), r = rows(t0)
      if (Number.isFinite(was)) r.push({ signal: "TK-200.level_pct", window: [t0 - W, t0], value: was, unit: "%/min", label: "Slope between transfers before the change (the line's draw)" })
      const ev = make("tank.rate_change", "TK-200", t0, t,
        { kind: "plateau", rate, net, levelT0: r[2].value, was, since: cause ? "stopped" : null, open: true },
        ["TK-200.level_pct"], r)
      link(cause, ev)
      tk.rateEv = emit(ev)
    } else if (rate < rules.tankResume) {
      tk.flow = "falling"
      const plateau = tk.rateEv; tk.rateEv = null
      if (!plateau) return
      const t0 = tk.firstFallT ?? t
      dataOf(plateau.id).open = false; plateau.t1 = t0; update(plateau)
      const cause = causeOf("line.restarted", t0)
      const resumed = tk.resumeN ? (tk.resumeSum / tk.resumeN) * 60 : rate      // the slope since the fall resumed
      const r = rows(t0)
      r.push({ signal: "TK-200.level_pct", window: [t0, t], value: resumed, unit: "%/min", label: "Slope between transfers since the fall resumed" })
      const ev = make("tank.rate_change", "TK-200", t0, t, { kind: "fall", rate: resumed, net, levelT0: r[2].value, since: cause ? "restarted" : null }, ["TK-200.level_pct"], r)
      link(cause, ev)
      emit(ev)
    }
  }

  /** The newest level sample at or before tq (from the last 10 min). */
  function levelAt(tq) {
    for (let i = tk.levels.length - 1; i >= 0; i--) if (tk.levels[i][0] <= tq + 1e-9) return tk.levels[i]
    return null
  }

  const heldNow = () => waiting.size > 0 && (tk.permissive === 0 || tk.interlock === 1 || !!tk.hihiEv)
  function clearsText() {
    if (tk.lastLine === 0) return "Clears when the line restarts."
    if (tk.interlock === 1 || tk.hihiEv) return "Clears when the high-high trip resets and TK-200 drains."
    return `Clears when TK-200 falls below ${limits.permissivePct} %.`
  }
  /** A station entering WAIT_TRANSFER while the permissive is false; confirmed on the next sample. */
  function heldSample(t) {
    if (tk.heldEv) {
      if (!heldNow()) return heldClose(t)
      const d = dataOf(tk.heldEv.id)
      let changed = false
      if (waiting.size > d.maxN) { d.maxN = waiting.size; changed = true }
      if (waiting.size !== d.n) { d.n = waiting.size; changed = true }
      if (Number.isFinite(tk.level) && tk.level > d.level) d.level = tk.level      // the highest level while held
      tk.heldEv.t1 = t
      heldRows(tk.heldEv)
      if (changed) update(tk.heldEv)
      return
    }
    const t0 = tk.heldPendT; tk.heldPendT = null
    if (!heldNow() || !Number.isFinite(tk.level)) return
    const start = t0 ?? t
    const ev = make("transfer.held", "TK-200", start, t,
      { t0: start, spellT0: start, t1: null, prevDur: 0, level: tk.level, levelStart: tk.level, levelNow: tk.level, n: waiting.size, maxN: waiting.size, clears: clearsText(), open: true },
      ["TK-200.permissive", "TK-200.level_pct", ...Array.from(waiting, (id) => `${id}.phase`)], [])
    heldRows(ev)
    link(tk.hihiEv || causeOf("tank.rate_change", start, (e) => dataOf(e.id).kind === "plateau") || causeOf("line.stopped", start), ev)
    tk.heldEv = emit(ev)
  }
  function heldClose(t) {
    const ev = tk.heldEv; tk.heldEv = null
    const d = dataOf(ev.id); d.t1 = t; d.open = false; ev.t1 = t
    heldRows(ev)
    update(ev)
  }
  /** Every figure the hold's text states, as evidence rows. */
  function heldRows(ev) {
    const d = dataOf(ev.id), t1 = ev._t1
    ev.evidence = [
      { signal: "TK-200.permissive", window: [ev.t0, t1], value: 0, unit: "", label: "Transfer permissive (false)" },
      { signal: "TK-200.level_pct", window: [ev.t0, ev.t0], value: d.levelStart, unit: "%", label: "Tank level when the hold began" },
      { signal: "TK-200.level_pct", window: [d.spellT0, d.spellT0], value: d.levelNow, unit: "%", label: "Tank level when this spell began" },
      { signal: "TK-200.level_pct", window: [ev.t0, t1], value: d.level, unit: "%", label: "Highest tank level while held" },
      { signal: "TK-200.permissive", window: [ev.t0, t1], value: d.maxN, unit: "stations", label: "Most stations waiting at once" },
      { signal: "TK-200.permissive", window: [t1, t1], value: d.n, unit: "stations", label: "Stations waiting now" },
      { signal: "TK-200.permissive", window: [ev.t0, t1], value: limits.permissivePct, unit: "%", label: "Permissive level" },
    ].filter((r) => Number.isFinite(r.value))
  }

  // ---- plant events ----
  function onPlantEvent(e) {
    switch (e.type) {
      case "unit.phase": {
        const u = units.get(e.unit); if (!u) return
        u.phase = e.to
        phasesSeen = true
        if (e.to === "DRAIN" || e.to === "EMPTY") { draining.add(u.id); tk.lastDrainT = e.t }
        else if (draining.delete(u.id)) tk.lastDrainT = e.t
        if (e.from === "DRIBBLE" && e.to === "SETTLE") { u.settleT = e.t; u.latchPending = true; u.stroke = NaN }
        if (e.to === "WAIT_TRANSFER") { waiting.add(u.id); if (!tk.heldEv && tk.heldPendT == null && heldNow()) tk.heldPendT = e.t }
        else if (e.from === "WAIT_TRANSFER") { waiting.delete(u.id); if (!waiting.size) { tk.heldPendT = null; if (tk.heldEv) heldClose(e.t) } }
        return
      }
      case "dose.verify": return onVerify(e)
      case "preact.trim": {
        const u = units.get(e.unit)
        if (u && u.pendingDose) Object.assign(dataOf(u.pendingDose.id), { trim: e.to - e.from, trimTo: e.to })
        return
      }
      case "batch.start": batchStart.set(e.batchId, e.t); trim(batchStart); return
      case "batch.complete": return onBatchComplete(e)
      case "tank.interlock":
        if (e.tripped) { if (!tk.hihiEv && tk.hihiPendT == null) tk.hihiPendT = e.t }
        else if (tk.hihiEv) hihiClose(e.t)
        else if (tk.hihiPendT != null) tk.hihiClearT = e.t
        return
      case "wear.rebuilt": {
        const u = units.get(e.unit); if (!u) return
        u.strokes = []; u.rebuildAt = e.t      // a new seal: the old close-time history no longer applies
        return
      }
      default: return                          // fault.*, alarm.*, tank.permissive...: not read (the detector finds its own)
    }
  }
  const trim = (m) => { while (m.size > 100) m.delete(m.keys().next().value) }

  function baseline(u) {
    const xs = u.strokes.slice(-rules.baselineN).map((p) => p.s)
    return xs.length >= rules.baselineMinN ? { value: median(xs), n: xs.length, from: u.strokes[u.strokes.length - xs.length].t } : null
  }

  function onVerify(e) {
    const u = units.get(e.unit); if (!u) return
    if (u.pendingDose) { emit(u.pendingDose); u.pendingDose = null }
    let doses = batchDoses.get(e.batchId); if (!doses) { batchDoses.set(e.batchId, (doses = [])); trim(batchDoses) }
    doses.push({ unit: e.unit, err: e.err, result: e.result })

    // Without the plant's phases (no close command seen) the latest reading is the only one there is.
    const closeT = u.settleT != null && e.t - u.settleT < 60 ? u.settleT : e.t
    const stroke = u.settleT == null ? u.tclose : closeT === u.settleT ? u.stroke : NaN
    u.stroke = NaN
    const base = baseline(u)
    const tc = `${u.id}.xv02_tclose`
    const strokeRows = []
    if (Number.isFinite(stroke)) {
      strokeRows.push({ signal: tc, window: [closeT, closeT + stroke], value: stroke, unit: "s", label: "Measured dribble close time" })
      if (base) strokeRows.push({ signal: tc, window: [base.from, e.t], value: base.value, unit: "s", label: `Baseline (median of the last ${base.n} strokes)` })
    }
    let slow = null
    if (Number.isFinite(stroke) && base && stroke > rules.slowCloseRatio * base.value) {
      slow = emit(make("valve.slow_close", u.id, closeT, closeT + stroke,
        { valve: u.valve, stroke, ratio: stroke / base.value }, [tc], strokeRows.map((r) => ({ ...r }))))
    }
    if (Number.isFinite(stroke)) {
      const typical = u.strokes.length ? median(u.strokes.map((p) => p.s)) : NaN
      if (!Number.isFinite(typical) || stroke <= rules.outlierRatio * typical) {
        u.strokes.push({ batchId: e.batchId, t: e.t, s: stroke })
        if (u.strokes.length > rules.wearFitN) u.strokes.shift()
      }
    }

    if (e.result !== "ok") {
      const cls = e.result === "overshoot" ? "dose.overshoot" : "dose.undershoot"
      // An opposite-sign miss right after a trimmed one is the trim overcorrecting; a same-sign run is a standing offset.
      const last = u.recovery[u.recovery.length - 1]
      const prev = last && byId.has(last.id) && Math.sign(dataOf(last.id).err) !== Math.sign(e.err) ? last : null
      const d = {
        unit: u.id, err: e.err, stroke, base: base ? base.value : NaN, trim: null, trimTo: NaN, backIn: 0,
        follows: prev ? { err: dataOf(prev.id).err, word: prev.class === "dose.overshoot" ? "overshoot" : "undershoot" } : null,
      }
      const ev = make(cls, u.id, closeT, e.t, d, [`${u.id}.wt`, tc],
        [{ signal: `${u.id}.wt`, window: [closeT, e.t], value: e.err, unit: "kg", label: `Verify error against ${e.sp} kg` }, ...strokeRows])
      link(slow, ev)
      link(prev, ev)          // the previous dose's preact trim overcorrected into this one
      u.recovery.push(ev)
      u.pendingDose = ev      // emitted on the next sample, once the plant's preact.trim for this verify has arrived
    } else if (u.recovery.length) {
      const k = u.recovery.length
      u.recovery.forEach((ev, i) => { dataOf(ev.id).backIn = k - i; update(ev) })
      u.recovery = []
    }
    wearCheck(u, e)
  }

  /** Least-squares close-time slope per batch over the last 60 strokes, projected to the valve's life limit. */
  function wearCheck(u, e) {
    if (u.strokes.length < rules.wearMinN) return
    const med = median(u.strokes.map((p) => p.s))
    const pts = u.strokes.filter((p) => p.s <= rules.outlierRatio * med)
    if (pts.length < rules.wearMinN) return
    let sx = 0, sy = 0, sxx = 0, sxy = 0
    for (const p of pts) { sx += p.batchId; sy += p.s; sxx += p.batchId * p.batchId; sxy += p.batchId * p.s }
    const n = pts.length, den = n * sxx - sx * sx
    if (!den) return
    const slope = (n * sxy - sx * sy) / den
    if (slope <= 1e-7) return
    const last = pts[n - 1]
    const due = Math.max(0, Math.ceil((limits.valveLifeS - last.s) / slope))
    const tc = `${u.id}.xv02_tclose`
    const rows = [
      { signal: tc, window: [pts[0].t, last.t], value: slope, unit: "s/batch", label: `Close-time slope over ${n} batches` },
      { signal: tc, window: [last.t, last.t], value: last.s, unit: "s", label: "Latest close time" },
      { signal: tc, window: [last.t, last.t], value: limits.valveLifeS, unit: "s", label: "Life limit" },
      { signal: tc, window: [last.t, last.t], value: due, unit: "batches", label: "Batches until the limit at this slope" },
    ]
    if (u.wearEv) {       // refreshed once per batch with the live projection; the first crossing stays t0
      const ev = u.wearEv
      Object.assign(dataOf(ev.id), { due, now: last.s })
      ev.t1 = e.t; ev.evidence = rows
      update(ev)
      return
    }
    if (due > limits.dueBatches) return
    u.wearEv = emit(make("wear.projection", u.id, e.t, e.t,
      { valve: u.valve, due, now: last.s, life: limits.valveLifeS, clearedAt: null, open: true }, [tc], rows))
  }

  function onBatchComplete(e) {
    const start = batchStart.get(e.batchId), doses = batchDoses.get(e.batchId) || []
    batchStart.delete(e.batchId); batchDoses.delete(e.batchId)
    const ok = doses.filter((x) => x.result === "ok").length
    let text
    if (!doses.length) text = "no dose verifies observed"
    else if (ok === doses.length && doses.length === units.size) text = `all ${WORDS[ok] || ok} doses in tolerance`
    else {
      const out = doses.filter((x) => x.result !== "ok").map((x) => `${x.unit} ${kg(x.err)}`)
      text = `${ok} of ${doses.length} doses in tolerance${out.length ? ` (${out.join(", ")})` : ""}`
    }
    const t0 = start != null ? start : e.t
    emit(make("batch.complete", null, t0, e.t, { batchId: e.batchId, kg: e.kg, doses: text, dur: start != null ? e.t - start : null },
      ["batch.id"], [{ signal: "batch.id", window: [t0, e.t], value: e.batchId, unit: "", label: "Batch" }]))
  }

  /** Every cause upstream and every effect downstream of an event (transitive), each list in time order. */
  function chainsFor(id) {
    const walk = (start, key) => {
      const seen = new Set([id]), out = [], queue = [...(byId.get(start)?.[key] || [])]
      while (queue.length) {
        const k = queue.shift()
        if (seen.has(k)) continue
        seen.add(k)
        const ev = byId.get(k); if (!ev) continue
        out.push(ev); queue.push(...ev[key])
      }
      return out.sort((a, b) => a.t0 - b.t0 || (a.id < b.id ? -1 : 1))
    }
    return { causes: walk(id, "causes"), effects: walk(id, "effects") }
  }

  return {
    events, feed, onPlantEvent, chainsFor,
    onDetect(fn) { listeners.add(fn); return () => listeners.delete(fn) },
    get signalIndex() { return index },
    get signalCount() { return signals.length },
  }
}

/**
 * Replay a recorded fixture ({signals, samples: [[t, ...values]], events}) through a detector, the way the page feeds
 * it: each second, the plant events up to that second, then the snapshot. Rows are written on change, so a missing
 * second repeats the previous row. Signals the fixture did not record read as NaN (rules that need them stay silent).
 */
export function replayFixture(det, fx) {
  const snap = new Float64Array(det.signalCount).fill(NaN)
  const cols = fx.signals.map((k) => det.signalIndex[k])
  const rows = fx.samples, evs = fx.events
  let ri = 0, ei = 0
  if (rows.length) {
    const tEnd = rows[rows.length - 1][0]
    for (let t = rows[0][0]; t <= tEnd; t += 1) {
      while (ri < rows.length && rows[ri][0] <= t) {
        const r = rows[ri++]
        for (let j = 0; j < cols.length; j++) if (cols[j] != null) snap[cols[j]] = r[j + 1]
      }
      while (ei < evs.length && evs[ei].t <= t) det.onPlantEvent(evs[ei++])
      det.feed(t, snap)
    }
  }
  while (ei < evs.length) det.onPlantEvent(evs[ei++])
  return det
}
